"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../../utils/api/axiosInstance.js";
import { logout } from "../../../../utils/api/auth.js";
import GlobalHeader from "../../../../components/GlobalHeader.jsx";
import {
  FaExclamationTriangle,
  FaArrowLeft,
  FaShoppingCart,
} from "react-icons/fa";
import "../css/ReviewPurchase.css";
import PurchaseCard from "../components/PurchaseCard.jsx";

const DEFAULT_EVENT_IMAGE = "/XPRESS-TICKETS-LOGO2.png";
const eventDetailsCache = {};

const normalizeEventRecords = (events) => {
  if (!Array.isArray(events)) {
    return [];
  }

  return events
    .map((event) => {
      const idCandidates = [
        event.event_id,
        event.eventId,
        event.EventID,
        event.EventId,
        event.id,
        event.eventid,
      ]
        .filter((candidate) => candidate !== undefined && candidate !== null)
        .map((candidate) => String(candidate));

      if (idCandidates.length === 0) {
        return null;
      }

      return {
        ids: idCandidates,
        eventImage:
          event.bannerimage ||
          event.image ||
          event.file_url ||
          event.cover_image_url ||
          event.coverimage ||
          DEFAULT_EVENT_IMAGE,
        background: event.background_images_array || event.background || null,
      };
    })
    .filter(Boolean);
};

const buildEventLookup = (events) => {
  const normalizedEvents = normalizeEventRecords(events);
  return normalizedEvents.reduce((lookup, event) => {
    event.ids.forEach((id) => {
      lookup[id] = event;
    });
    return lookup;
  }, {});
};

const fetchAvailableEventsLookup = async () => {
  try {
    const response = await axiosInstance.get("/shared/events/available");
    return buildEventLookup(response.data);
  } catch (err) {
    console.error("Error fetching available events for lookup:", err);
    return {};
  }
};

const fetchEventDetailsById = async (eventId) => {
  if (!eventId) {
    return null;
  }

  const cacheKey = String(eventId);
  if (Object.prototype.hasOwnProperty.call(eventDetailsCache, cacheKey)) {
    return eventDetailsCache[cacheKey];
  }

  try {
    const response = await axiosInstance.get(`/shared/events/${cacheKey}`);
    const event = response.data;

    if (!event) {
      eventDetailsCache[cacheKey] = null;
      return null;
    }

    const normalized = {
      eventImage:
        event.bannerimage ||
        event.image ||
        event.file_url ||
        event.cover_image_url ||
        event.coverimage ||
        DEFAULT_EVENT_IMAGE,
      background: event.background_images_array || event.background || null,
    };

    eventDetailsCache[cacheKey] = normalized;
    return normalized;
  } catch (err) {
    console.error(`Error fetching event details for ${cacheKey}:`, err);
    eventDetailsCache[cacheKey] = null;
    return null;
  }
};

const ReviewPurchase = () => {
  const navigate = useNavigate();
  const [purchasedTickets, setPurchasedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPurchasedTickets = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        let currentUserId = null;

        if (!token) {
          setError("No authentication token found");
          navigate("/");
          return;
        }

        try {
          const tokenPayload = JSON.parse(atob(token.split(".")[1]));
          currentUserId = tokenPayload.userId || tokenPayload.user_id || tokenPayload.sub;
        } catch (e) {
          console.error("Error parsing JWT token:", e);
          setError("Invalid authentication token");
          navigate("/");
          return;
        }

        if (!currentUserId) {
          setError("User not authenticated");
          navigate("/");
          return;
        }

        console.log('[ReviewPurchase] 📋 Fetching purchases — API: GET /customer/ticket-purchases');
        const response = await axiosInstance.get("/customer/ticket-purchases");
        const data = response.data;
        console.log('[ReviewPurchase] ✅ Purchases loaded:', {
          columns: 'purchase_id/purchaseId/id, user_id/userId, event_id/eventId, event_name/eventName, event_location/eventLocation, event_date/eventDate, file_url/eventImage, amount/price, number_of_tickets/quantity, delegate_details, purchase_date',
          total: data?.length,
        });

        const currentUserIdNum = Number(currentUserId);
        const userPurchases = data.filter((purchase) => {
          const purchaseUserId = Number(purchase.user_id || purchase.userId);
          return purchaseUserId === currentUserIdNum;
        });

        const uniquePurchases = userPurchases.reduce((acc, current) => {
          if (!current.purchase_id && !current.purchaseId) {
            return acc;
          }

          const existingIndex = acc.findIndex(
            (item) =>
              (item.purchase_id && item.purchase_id === current.purchase_id) ||
              (item.purchaseId && item.purchaseId === current.purchaseId) ||
              (item.id && item.id === current.id),
          );

          if (existingIndex === -1) {
            acc.push(current);
          }
          return acc;
        }, []);

        const eventGroups = {};
        uniquePurchases.forEach((purchase) => {
          const eventId = purchase.event_id || purchase.eventId;
          const eventName = purchase.event_name || purchase.eventName || "Unknown Event";
          const eventLocation = purchase.event_location || purchase.eventLocation || "Location not specified";
          const eventDate = purchase.event_date || purchase.eventDate || null;
          const eventImage = purchase.file_url || purchase.eventImage || "/XPRESS-TICKETS-LOGO2.png";
          const amount = Number.parseFloat(purchase.amount || purchase.price || "0");

          if (!eventId) {
            return;
          }

          if (!eventGroups[eventId]) {
            eventGroups[eventId] = {
              eventId,
              eventName,
              eventLocation,
              eventDate,
              eventImage,
              background: null,
              purchases: [],
              totalTickets: 0,
              totalAmount: 0,
            };
          }

          let delegateCount = 1;
          let delegates = [];
          const delegateDetails = purchase.delegate_details || purchase.delegateDetails;

          if (delegateDetails) {
            try {
              if (typeof delegateDetails === "string") {
                const trimmed = delegateDetails.trim();
                if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
                  delegates = JSON.parse(trimmed);
                } else {
                  delegates = [{ name: trimmed }];
                }
              } else if (Array.isArray(delegateDetails)) {
                delegates = delegateDetails;
              } else if (delegateDetails && typeof delegateDetails === "object") {
                delegates = [delegateDetails];
              }

              if (!Array.isArray(delegates)) {
                delegates = delegates ? [delegates] : [];
              }

              delegateCount = Math.max(1, delegates.length);
            } catch (e) {
              console.error("Error parsing delegate details:", e);
              delegateCount = purchase.number_of_tickets || purchase.quantity || 1;
            }
          } else {
            delegateCount = purchase.number_of_tickets || purchase.quantity || 1;
          }

          const cleanPurchase = {
            ...purchase,
            delegates:
              delegates.length > 0
                ? delegates
                : [
                    {
                      name: purchase.customer_name || purchase.customerName || "Guest",
                      email: purchase.customer_email || purchase.customerEmail || "",
                      phone: purchase.customer_phone || purchase.customerPhone || "",
                    },
                  ],
          };

          eventGroups[eventId].purchases.push(cleanPurchase);
          eventGroups[eventId].totalTickets += delegateCount;
          eventGroups[eventId].totalAmount += amount;
        });

        const groupedTickets = Object.values(eventGroups);

        let eventLookup = {};
        if (groupedTickets.length > 0) {
          console.log('[ReviewPurchase] 🖼️ Enriching images — API: GET /shared/events/available');
          eventLookup = await fetchAvailableEventsLookup();
        }

        const groupedTicketsWithImages = await Promise.all(
          groupedTickets.map(async (eventGroup) => {
            const lookupKey = String(eventGroup.eventId);
            let eventDetails = eventLookup[lookupKey];

            if (!eventDetails) {
              eventDetails = await fetchEventDetailsById(lookupKey);
            }

            if (eventDetails) {
              return {
                ...eventGroup,
                eventImage: eventDetails.eventImage || eventGroup.eventImage || DEFAULT_EVENT_IMAGE,
                background: eventDetails.background ?? eventGroup.background ?? null,
              };
            }

            return {
              ...eventGroup,
              eventImage: eventGroup.eventImage || DEFAULT_EVENT_IMAGE,
            };
          }),
        );

        const sortedTickets = groupedTicketsWithImages.sort((a, b) => {
          const aDate = getLatestPurchaseDate(a.purchases);
          const bDate = getLatestPurchaseDate(b.purchases);
          return bDate - aDate;
        });

        console.log('[ReviewPurchase] ✅ Event groups ready:', {
          eventGroups: sortedTickets.map((g) => ({ eventId: g.eventId, eventName: g.eventName, totalTickets: g.totalTickets, totalAmount: g.totalAmount, purchaseCount: g.purchases.length })),
          note: 'totalAmount = SUM(purchase.amount) — stored as full paid amount (flat + service fee)',
        });
        setPurchasedTickets(sortedTickets);
      } catch (err) {
        console.error("Error fetching purchased tickets:", err);
        setError(`Failed to load purchased tickets: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchPurchasedTickets();
  }, [navigate]);

  const getLatestPurchaseDate = (purchases) => {
    const validDates = purchases
      .map((p) => {
        const date = new Date(p.purchase_date);
        return Number.isNaN(date.getTime()) ? null : date.getTime();
      })
      .filter((date) => date !== null);

    return validDates.length > 0 ? Math.max(...validDates) : 0;
  };

  const getFormattedLatestDate = (purchases) => {
    const latestTimestamp = getLatestPurchaseDate(purchases);
    if (latestTimestamp === 0) {
      return "Date not available";
    }
    return formatDate(new Date(latestTimestamp).toISOString());
  };

  const handleViewTickets = (eventGroup) => {
    const processedPurchases = eventGroup.purchases.map((purchase) => ({
      ...purchase,
      purchase_id: purchase.purchase_id || `temp-${Math.random().toString(36).substr(2, 9)}`,
      event_id: purchase.event_id || eventGroup.eventId,
      user_id: purchase.user_id || null,
      number_of_tickets: purchase.number_of_tickets || 1,
      amount: purchase.amount || 0,
      status: purchase.status || "completed",
      package: purchase.package || {},
      delegate_details: purchase.delegate_details || [],
      event_name: purchase.event_name || eventGroup.eventName,
      event_date: purchase.event_date || eventGroup.eventDate,
      event_location: purchase.event_location || eventGroup.eventLocation,
      file_url: purchase.file_url || eventGroup.eventImage,
    }));

    navigate("/ticketslist", {
      state: {
        eventId: eventGroup.eventId,
        eventName: eventGroup.eventName,
        totalTickets: eventGroup.totalTickets,
        totalAmount: eventGroup.totalAmount,
        purchases: processedPurchases,
        eventInfo: {
          eventName: eventGroup.eventName,
          eventLocation: eventGroup.eventLocation,
          eventDate: eventGroup.eventDate,
          eventImage: eventGroup.eventImage,
          eventId: eventGroup.eventId,
          time: eventGroup.eventTime || "",
        },
      },
      replace: true,
    });
  };

  const handleBackToDashboard = () => {
    navigate("/customerdash");
  };

  const handleLogout = () => {
    logout(navigate);
  };

  const formatPrice = (price) => {
    const numPrice = Number.parseFloat(price);
    return Number.isNaN(numPrice) ? "R 0.00" : `R ${numPrice.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date not available";
    try {
      return new Date(dateString).toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return "Date not available";
    }
  };

  const handleBrowseEvents = () => {
    navigate("/eventmenu");
  };

  return (
    <div className="purchases-page">
      <GlobalHeader
        title="Purchased Tickets"
        subtitle="Review your event purchases"
        backTo="/customerdash"
        logoutTo="/"
        onBack={handleBackToDashboard}
        onLogout={handleLogout}
      />

      <main className="purchases-main">
        {loading ? (
          <div className="purchases-loading">
            <div className="purchases-loading-card">
              <div className="purchases-loading-spinner" />
              <p className="purchases-loading-text">Loading your tickets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="purchases-error">
            <div className="purchases-error-card">
              <div className="purchases-error-icon">
                <FaExclamationTriangle />
              </div>
              <h3 className="purchases-error-title">Error Loading Tickets</h3>
              <p className="purchases-error-message">{error}</p>
              <button
                type="button"
                className="purchases-retry-button"
                onClick={() => window.location.reload()}
              >
                <FaArrowLeft /> Try Again
              </button>
            </div>
          </div>
        ) : purchasedTickets.length > 0 ? (
          <div className="purchases-grid">
            {purchasedTickets.map((eventGroup) => (
              <PurchaseCard
                key={eventGroup.eventId}
                eventGroup={eventGroup}
                formatDate={formatDate}
                getFormattedLatestDate={getFormattedLatestDate}
                formatPrice={formatPrice}
                onViewTickets={handleViewTickets}
              />
            ))}
          </div>
        ) : (
          <div className="purchases-empty">
            <div className="purchases-empty-card">
              <div className="purchases-empty-icon">
                <FaShoppingCart />
              </div>
              <h3 className="purchases-empty-title">No Purchases Yet</h3>
              <p className="purchases-empty-message">
                You don't have any ticket purchases yet. Browse our events and get your first ticket.
              </p>
              <button
                type="button"
                className="purchases-browse-button"
                onClick={handleBrowseEvents}
              >
                Browse Events
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReviewPurchase;
